import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Button, IconButton } from '@ui/components/Button'
import { PageSection } from '@ui/components/PageSection'
import { ArrowsDownUpIcon } from '@ui/icons/ArrowsDownUp.icon'
import { EditIcon } from '@ui/icons/Edit.icon'
import { UserGroupIcon } from '@ui/icons/UserGroup.icon'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { TableBulkActions } from '@ui/v2/Table/Table'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'
import { TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { CreateTradingAccountIntroducingBrokerRebateGroupBulk } from '@model/CreateTradingAccountIntroducingBrokerRebateGroupBulk'
import { GetUpdateIntroducingBrokerTradingAccountRebateBulk } from '@model/GetUpdateIntroducingBrokerTradingAccountRebateBulk'
import { NameDto } from '@model/NameDto'
import { TradingAccountRebateDetailedDto } from '@model/TradingAccountRebateDetailedDto'

import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { ConfirmModal } from '@shared/modals/Confirm.modal'
import { InformationModal } from '@shared/modals/Information.modal'

import { GroupFormValues, GroupModal } from '../Group.modal'
import { useCreateOrUpdateRebateGroup } from '../hooks/useCreateOrUpdateRebateGroup'
import { ClientsPageTable } from './ClientsPageTable'
import { useAddIBTradingAccountsRebateGroups } from './hooks/useAddIBTradingAccountsRebateGroups'
import { useIBClientFilterOptions } from './hooks/useIBClientFilterOptions'
import { useIBRebateClientsSummary } from './hooks/useIBRebateClientsSummary'
import {
  TradingAccountsRebateSortOptions,
  useIBTradingAccountsRebate,
} from './hooks/useIBTradingAccountsRebate'
import { useResetRebateSettings } from './hooks/useResetRebateSettings'
import { useUpdateIBTradingAccountsRebates } from './hooks/useUpdateIBTradingAccountsRebates'
import { AddToGroupFormValues, AddToGroupModal } from './modals/AddToGroup.modal'
import { NoGroupModal } from './modals/NoGroup.modal'
import { RebateSettingsFormValues, RebateSettingsModal } from './modals/RebateSettings.modal'

interface IIBRebatesClientsPage {
  isRebatesManagement?: boolean
}

export const IBRebatesClientsPage = ({ isRebatesManagement }: IIBRebatesClientsPage) => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const isMobile = useWindowResize()
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isGroupsLoading, setIsGroupsLoading] = useState(false)
  const [selectedRebateGroupOption, setSelectedRebateGroupOption] =
    useState<Partial<AddToGroupFormValues>>()
  const confirmResetModal = useModal(ConfirmModal, {
    title: t('Please, confirm your action'),
    description: t('IB.RebatesPage.ClientsPage.You are about to reset Rebate setting'),
  })

  const rebateChangeModal = useModal(InformationModal, {
    title: t('IB.RebatesPage.ClientsPage.Rebate type change'),
    description: t(
      'IB.RebatesPage.ClientsPage.You’ve changed rebate payout type. It will be reflected in all future payouts. Current and/or previous payouts for approvals will remain as is in Approvals section'
    ),
  })

  const addToGroupModal = useModal(AddToGroupModal)
  const {
    accountTypes,
    tradingAccounts,
    clients,
    groups: groupFilterOptions,
    signupDateShortcutOptions,
    countries,
    rebateApprovalTypes,
    rebatePeriods,
  } = useIBClientFilterOptions()

  const noGroupModal = useModal(NoGroupModal, {
    onCreate: () => {
      noGroupModal.close()
      groupModal.open({
        groupData: undefined,
        isCreateGroup: true,
      })
    },
  })

  const rebateSettingsModal = useModal(RebateSettingsModal, {
    approvalTypes: rebateApprovalTypes,
    rebatePeriods: rebatePeriods,
  })

  const {
    query: tableQuery,
    sortOptions,
    search: tableSearch,
    setSearch: setTableSearch,
    forceRefresh: forceRefreshAccounts,
  } = useIBTradingAccountsRebate(
    isRebatesManagement
      ? {
          pageSize: 5,
        }
      : undefined
  )

  const {
    data: summaryItems,
    forceRefresh: refreshSummaryItems,
    isLoading: isSummaryLoading,
  } = useIBRebateClientsSummary(tableSearch)

  const isAnyFilterActive = useMemo(
    () => Object.values(tableSearch || {}).some((value) => !!value),
    [tableSearch]
  )

  const isTableLoading = tableQuery.isLoading

  const refreshData = () => {
    forceRefreshAccounts()
    refreshSummaryItems()
    setSelectedIds([])
  }

  const { resetRebateSettings, resetAllRebateSettings } = useResetRebateSettings({
    onCompleted: () => {
      refreshData()
      confirmResetModal.close()
    },
  })

  const { updateRebates } = useUpdateIBTradingAccountsRebates({
    onCompleted: refreshData,
  })

  const { addToGroups } = useAddIBTradingAccountsRebateGroups({
    onCompleted: refreshData,
  })

  const { createOrUpdateRebateGroup } = useCreateOrUpdateRebateGroup()

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    tableQuery.setPageQuery?.({
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const handleCheckBoxClick = () => {
    if (isBulkEditMode) {
      setSelectedIds([])

      return
    }

    setSelectedIds(tableQuery.data.map((item) => item.tradingAccount.id))
  }

  const handleAddToGroupClick = async () => {
    const ibId = account?.clientIntroducingBroker?.id
    if (!ibId || isGroupsLoading) return

    setIsGroupsLoading(true)
    try {
      const { items: groups } = await apiClient.getIBRebateGroups(ibId, {
        pageSize: 999,
        caller: 'ca',
        languageId: locale,
      })

      if (!groups.length) {
        noGroupModal.open()
      } else {
        addToGroupModal.open({
          onSubmit: handleAddToGroupSubmit,
          initialValues: selectedRebateGroupOption,
          groups,
        })
      }
    } catch (error) {
      console.error('Failed to fetch rebate groups', error)
    } finally {
      setIsGroupsLoading(false)
    }
  }

  const handleResetAllRebates = () => {
    confirmResetModal.open({
      onConfirm: () => resetAllRebateSettings(selectedIds),
    })
  }

  const handleEditClick = (client: TradingAccountRebateDetailedDto) => {
    const rebateSettings = {
      rebateAmountInPercentage: client?.rebateAmountInPercentage
        ? String(client.rebateAmountInPercentage)
        : undefined,
      rebateApprovalTypeId: client.rebateApprovalType?.id ?? undefined,
      rebatePeriodId: client.rebatePeriod?.id ?? undefined,
      tradingAccountId: client.tradingAccount.id,
    }
    rebateSettingsModal.open({
      onSubmit: handleRebateSettingsSubmit,
      initialValues: rebateSettings,
    })
  }

  const handleAddToGroupModalClose = () => {
    addToGroupModal.close()
    groupModal.close()
    setSelectedRebateGroupOption(undefined)
    setSelectedIds([])
  }

  const handleRebateSettingsSubmit = async (values: Partial<RebateSettingsFormValues>) => {
    const accountIds = values.tradingAccountId ? [values.tradingAccountId] : selectedIds
    if (
      values.rebateApprovalTypeId === undefined ||
      values.rebatePeriodId === undefined ||
      !values.rebateAmountInPercentage ||
      !account?.clientIntroducingBroker?.id ||
      accountIds.length === 0
    ) {
      return
    }

    const payload: GetUpdateIntroducingBrokerTradingAccountRebateBulk = {
      tradingAccountIds: accountIds,
      rebateApprovalTypeId: values.rebateApprovalTypeId,
      rebatePeriodId: values.rebatePeriodId,
      rebateAmountInPercentage: parseFloat(values.rebateAmountInPercentage),
      introducingBrokerId: account.clientIntroducingBroker.id,
    }

    const response = await updateRebates(payload)

    rebateSettingsModal.close()

    if (response?.skipped.length === 0) {
      const hasManualApproval = accountIds.some((id) => {
        const client = tableQuery.data.find((item) => item.tradingAccount.id === id)
        return client?.rebateApprovalType?.id === 1
      })

      if (hasManualApproval && values.rebateApprovalTypeId === 2) {
        rebateChangeModal.open()
      }
    }
  }

  const handleAddToGroupSubmit = async (values: Partial<AddToGroupFormValues>) => {
    if (!values.groupId || !account?.clientIntroducingBroker?.id || selectedIds.length === 0) {
      return
    }

    const payload: CreateTradingAccountIntroducingBrokerRebateGroupBulk = {
      tradingAccountIds: selectedIds,
      groupId: values.groupId,
      introducingBrokerId: account.clientIntroducingBroker.id,
    }

    await addToGroups(payload)
    addToGroupModal.close()
    setSelectedRebateGroupOption(undefined)
    setSelectedIds([])
  }

  const handleCreateGroupSubmit = async (values: Partial<GroupFormValues>) => {
    if (
      values.name &&
      values.rebateAmountInPercentage &&
      values.rebateApprovalTypeId &&
      values.rebatePeriodId !== undefined
    ) {
      await createOrUpdateRebateGroup({
        name: values.name,
        rebateAmountInPercentage: values.rebateAmountInPercentage,
        rebateApprovalTypeId: values.rebateApprovalTypeId,
        rebatePeriodId: values.rebatePeriodId,
        isDefault: !!values.isDefault,
      })
    }

    handleAddToGroupModalClose()
    refreshData()
  }

  const groupModal = useModal(GroupModal, {
    onSubmit: handleCreateGroupSubmit,
    approvalTypes: rebateApprovalTypes,
    rebatePeriods: rebatePeriods,
  })
  const isBulkEditMode = selectedIds.length > 0

  return (
    <div>
      {!isRebatesManagement && (
        <TableSummary isLoading={isSummaryLoading} summaryItems={summaryItems} />
      )}

      {!isRebatesManagement && (
        <PageSection
          title={t('IB.RebatesPage.Trading accounts')}
          id={ScrollToIds.IBRebateClientsHeader}
          onFilterClick={() => setIsFilterVisible((prevState) => !prevState)}
          isFilterButtonDisabled={isBulkEditMode}
        />
      )}

      {!isBulkEditMode && (
        <TableFilter
          isVisible={isRebatesManagement || isFilterVisible}
          searchFilters={tableSearch}
          tradingAccountTypeOptions={accountTypes}
          dateShortcutOptions={signupDateShortcutOptions}
          countryOptions={countries}
          showRebateCheckbox
          tradingAccountSearchProps={{
            options: tradingAccounts,
          }}
          clientsSearchProps={{
            options: clients,
          }}
          groupSearchProps={{
            options: groupFilterOptions,
          }}
          filterOptions={
            isRebatesManagement
              ? [
                  FilterOptions.SignedUpDate,
                  FilterOptions.TotalCommission,
                  FilterOptions.RebateAmount,
                ]
              : [
                  FilterOptions.TradingAccountId,
                  FilterOptions.ClientId,
                  FilterOptions.TradingAccountType,
                  FilterOptions.SignedUpDate,
                  FilterOptions.Country,
                  FilterOptions.RebateGroupId,
                  FilterOptions.VolumeFxm,
                  FilterOptions.VolumeOther,
                  FilterOptions.TotalCommission,
                  FilterOptions.RebateAmount,
                ]
          }
          customLabels={{
            [FilterOptions.RebateGroupId]: t('IB.RebatesPage.ClientsPage.Group'),
          }}
          setSearchFilters={(searchFilters) => {
            if (!searchFilters) {
              return
            }

            setTableSearch(searchFilters)

            tableQuery.setPageQuery?.({
              ...tableQuery.pageQuery,
              pageIndex: 1,
            })
          }}
          size={isRebatesManagement ? 'sm' : 'md'}
        />
      )}

      <ClientsPageTable
        isRebatesManagement={isRebatesManagement}
        isAnyFilterActive={isAnyFilterActive}
        tableQuery={isRebatesManagement ? filterTableQuery(tableQuery) : tableQuery}
        sortOptions={isRebatesManagement ? filterSortOptions(sortOptions) : sortOptions}
        sticky='left'
        onSelect={setSelectedIds}
        selectedIds={selectedIds}
        onEdit={handleEditClick}
        onReset={(client) =>
          confirmResetModal.open({ onConfirm: () => resetRebateSettings(client) })
        }
        topContent={
          isBulkEditMode && (
            <TableBulkActions
              selectedCount={selectedIds.length}
              isCheckboxActive={isBulkEditMode}
              isCheckboxDisabled={isTableLoading || !tableQuery.data.length}
              onCheckboxClick={handleCheckBoxClick}
            >
              {!isMobile ? (
                <>
                  <Button.Tertiary
                    size='sm'
                    iconLeft={EditIcon}
                    disabled={!selectedIds.length}
                    onClick={() =>
                      rebateSettingsModal.open({
                        onSubmit: handleRebateSettingsSubmit,
                      })
                    }
                  >
                    {t('IB.RebatesPage.ClientsPage.Edit Rebate')}
                  </Button.Tertiary>

                  <Button.Tertiary
                    size='sm'
                    iconLeft={UserGroupIcon}
                    disabled={!selectedIds.length || isGroupsLoading}
                    loading={isGroupsLoading}
                    onClick={handleAddToGroupClick}
                  >
                    {t('IB.RebatesPage.ClientsPage.Add to Group')}
                  </Button.Tertiary>
                  <Button.Tertiary
                    size='sm'
                    iconLeft={ArrowsDownUpIcon}
                    disabled={!selectedIds.length}
                    onClick={handleResetAllRebates}
                  >
                    {t('Reset')}
                  </Button.Tertiary>
                </>
              ) : (
                <>
                  <IconButton.Tertiary
                    size='sm'
                    icon={EditIcon}
                    disabled={!selectedIds.length}
                    onClick={() =>
                      rebateSettingsModal.open({
                        onSubmit: handleRebateSettingsSubmit,
                      })
                    }
                  />

                  <IconButton.Tertiary
                    size='sm'
                    icon={UserGroupIcon}
                    disabled={!selectedIds.length || isGroupsLoading}
                    onClick={handleAddToGroupClick}
                  />
                  <IconButton.Tertiary
                    size='sm'
                    icon={ArrowsDownUpIcon}
                    disabled={!selectedIds.length}
                    onClick={handleResetAllRebates}
                  />
                </>
              )}

              <Button.Tertiary size='sm' onClick={() => setSelectedIds([])}>
                {t('IB.RebatesPage.ClientsPage.Cancel')}
              </Button.Tertiary>
            </TableBulkActions>
          )
        }
      />

      {tableQuery.meta && tableQuery.hasInitialResults && (
        <Pagination
          scrollToHeaderId={ScrollToIds.IBRebateClientsHeader}
          maxPageSize={100}
          pageData={tableQuery.meta}
          isLoading={tableQuery.isLoading}
          onPageChanged={handlePageChange}
          hidePageSize={isRebatesManagement}
        />
      )}
    </div>
  )
}

const filterTableQuery = (tableQuery: PaginatedQueryResult<TradingAccountRebateDetailedDto>) => {
  const TABLE_QUERY_DATA_COLUMNS = [
    'tradingAccount',
    'tradingAccountSignedUpDate',
    'totalCommission',
    'rebateAmountInPercentage',
    'rebateApprovalType',
    'rebatePeriod',
    'rebateGroup',
  ]

  const filteredData = tableQuery.data.map((dataItem) => {
    return Object.fromEntries(
      Object.entries(dataItem).filter(([key]) => TABLE_QUERY_DATA_COLUMNS.includes(key))
    )
  })

  return { ...tableQuery, data: filteredData }
}

const filterSortOptions = (sortOptions: NameDto<TradingAccountsRebateSortOptions | null>[]) => {
  const FILTER_SORT_OPTIONS_COLUMNS = [
    'TradingAccountName',
    'TradingAccountSignedUpDate',
    'TotalCommission',
    'RebateAmountInPercentage',
  ]

  return sortOptions.filter((option) => FILTER_SORT_OPTIONS_COLUMNS.includes(option.id as string))
}
